import type { GuestBookingPayload } from "./BookingSummary";

// Recovery for the guest booking flow across a full-page 3DS/SCA redirect.
//
// `PaymentStep` calls `stripe.confirmSetup({ redirect: "if_required" })`. Most cards confirm
// in-page, but a card issuer that requires an off-page authentication step causes the browser to
// fully navigate away (to the bank's challenge page) and back to `return_url`. That full
// navigation wipes every bit of in-memory React state: Form.tsx/AIView.tsx's step machine,
// formik's form values, and PaymentStep's own component state. Without recovery, the guest lands
// back on a blank FORM step and has to retype everything.
//
// We persist just enough here — the already-built booking payload, the captcha token used to
// enter the PAYMENT step, and the Stripe customer id for this SetupIntent — to resume and
// complete the booking on return, without asking the guest to retype anything or re-enter card
// details.
//
// stripeCustomerId has to be persisted explicitly (not re-read from Stripe.js on return):
// `stripe.retrieveSetupIntent()` — the client-side, publishable-key call — doesn't expose the
// `customer` field on the returned SetupIntent at all (only the secret-key server SDK does), so
// there's no way to recover it after a redirect other than stashing it ourselves beforehand.
const STORAGE_KEY = "dining-mate:pending-booking";

type PersistedPendingBooking = {
    payload: GuestBookingPayload;
    captchaToken: string;
    stripeCustomerId: string;
};

export const persistPendingBooking = (
    payload: GuestBookingPayload,
    captchaToken: string,
    stripeCustomerId: string,
): void => {
    try {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ payload, captchaToken, stripeCustomerId }),
        );
    } catch {
        // sessionStorage can throw (private browsing, storage full, etc.) — the guest simply
        // won't get the resume-after-redirect recovery. The normal card-entry flow is unaffected.
    }
};

export const readPendingBooking = (): PersistedPendingBooking | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as PersistedPendingBooking;
    } catch {
        return null;
    }
};

export const clearPendingBooking = (): void => {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Best effort — see persistPendingBooking.
    }
};

// Stripe appends `setup_intent_client_secret` (plus `setup_intent` and `redirect_status`) as
// query params on `return_url` only when it actually redirected the guest off-page to complete
// authentication (https://docs.stripe.com/payments/save-and-reuse?payment-ui=elements). Its
// presence is how we detect "we're back from a 3DS redirect" vs. a normal first visit.
export const getResumeSetupIntentClientSecret = (): string | null => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("setup_intent_client_secret");
};

// Strips the Stripe redirect-return query params once we've consumed them, so a manual refresh
// or bookmarked URL doesn't repeatedly attempt to resume the same (already-cleared) booking.
export const clearResumeQueryParams = (): void => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("setup_intent_client_secret");
    url.searchParams.delete("setup_intent");
    url.searchParams.delete("redirect_status");
    window.history.replaceState(null, "", url.toString());
};
